import { Card, Skeleton } from '@startupquickstart/frontend';
import { Eye, EyeOff, Images, Star } from 'lucide-react';
import { useMemo } from 'react';

import {
  useCuratePhoto,
  useSlideshowPhotos,
  type SlideshowPhoto,
} from '@/components/events/live/useSlideshowCuration';
import { cn } from '@/lib/utils';

interface SlideshowCurationPanelProps {
  eventId: string;
  className?: string;
}

/**
 * Choose what goes in the post-event slideshow, and in what order.
 *
 *   > We may want a better admin flow of deciding what goes in it and in
 *   > what order, with a sane default.
 *
 * The sane default needs no interaction at all: every correct photo plays
 * oldest-first, exactly as before. This panel exists for the two things a
 * facilitator actually does minutes before a room watches — drop the
 * thumb-over-the-lens shots, and pull two or three great ones to the
 * front.
 *
 * Deliberately not a drag-and-drop reorder of two hundred photos. Nobody
 * ranks a full reel between the last challenge and the closing remarks;
 * they exclude a few and feature a few.
 */
export function SlideshowCurationPanel({ eventId, className }: SlideshowCurationPanelProps) {
  const { data, isLoading } = useSlideshowPhotos(eventId);
  const curate = useCuratePhoto(eventId);

  const photos = useMemo(() => data?.data.photos ?? [], [data]);
  const included = photos.filter((p) => !p.slideshowExcluded);
  const featured = included.filter((p) => p.slideshowOrder !== null);

  const header = (
    <div className="flex items-center justify-between border-b px-4 py-3">
      <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
        Slideshow
      </h3>
      <span className="text-xs text-muted-foreground">
        {included.length} of {photos.length} playing
        {featured.length > 0 ? ` · ${featured.length} featured` : ''}
      </span>
    </div>
  );

  if (isLoading) {
    return (
      <Card className={cn('gap-0 overflow-hidden py-0', className)}>
        {header}
        <Skeleton className="h-48 rounded-none" />
      </Card>
    );
  }

  if (photos.length === 0) {
    return (
      <Card className={cn('gap-0 overflow-hidden py-0', className)}>
        {header}
        <div className="flex h-40 flex-col items-center justify-center text-muted-foreground">
          <Images className="mb-1 h-6 w-6" />
          <span className="text-sm">No photos submitted yet</span>
        </div>
      </Card>
    );
  }

  const nextFeatureOrder = featured.length + 1;

  return (
    <Card className={cn('gap-0 overflow-hidden py-0', className)}>
      {header}

      {/* Order shown is the order it will play — this reads the same
          endpoint the projector does, so the two can't disagree. */}
      <ul className="grid max-h-96 grid-cols-2 gap-3 overflow-auto p-4 sm:grid-cols-3 lg:grid-cols-4">
        {photos.map((photo: SlideshowPhoto) => {
          const isFeatured = photo.slideshowOrder !== null;
          return (
            <li key={photo.id} className="space-y-1">
              <div
                className={cn(
                  'relative aspect-square overflow-hidden rounded-lg border bg-muted',
                  photo.slideshowExcluded && 'opacity-40',
                )}
              >
                <img
                  src={photo.photoUrl}
                  alt={`${photo.teamName} — ${photo.challengeName}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                {isFeatured && (
                  <span className="absolute left-1 top-1 rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    #{photo.slideshowOrder}
                  </span>
                )}
              </div>

              <p className="truncate text-xs text-muted-foreground" title={photo.teamName}>
                {photo.teamName}
              </p>

              <div className="flex gap-1">
                <button
                  type="button"
                  disabled={curate.isPending}
                  onClick={() =>
                    curate.mutate({
                      submissionId: photo.id,
                      excluded: !photo.slideshowExcluded,
                    })
                  }
                  aria-label={
                    photo.slideshowExcluded
                      ? `Include ${photo.teamName}'s photo`
                      : `Exclude ${photo.teamName}'s photo`
                  }
                  className="flex flex-1 items-center justify-center gap-1 rounded border px-1.5 py-1 text-[11px] hover:bg-muted disabled:opacity-50"
                >
                  {photo.slideshowExcluded ? (
                    <>
                      <EyeOff className="h-3 w-3" /> Hidden
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" /> Playing
                    </>
                  )}
                </button>

                <button
                  type="button"
                  disabled={curate.isPending || photo.slideshowExcluded}
                  onClick={() =>
                    curate.mutate({
                      submissionId: photo.id,
                      // Toggling off returns it to chronological position
                      // rather than leaving a gap in the running order.
                      order: isFeatured ? null : nextFeatureOrder,
                    })
                  }
                  aria-label={
                    isFeatured
                      ? `Unfeature ${photo.teamName}'s photo`
                      : `Feature ${photo.teamName}'s photo`
                  }
                  className={cn(
                    'flex items-center justify-center rounded border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50',
                    isFeatured && 'border-amber-400 bg-amber-50 dark:bg-amber-950/30',
                  )}
                >
                  <Star className={cn('h-3 w-3', isFeatured && 'fill-amber-500 text-amber-500')} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
